import React from 'react';
import {
  Snackbar,
  Alert,
  AlertTitle,
  Button,
  IconButton,
  Slide,
  SlideProps,
  SnackbarCloseReason,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { ToastNotification, ToastSeverity } from '../../types/errors';

interface ToastProps {
  notification: ToastNotification;
  onClose: (id: string) => void;
  position?: {
    vertical: 'top' | 'bottom';
    horizontal: 'left' | 'center' | 'right';
  };
}

/**
 * Slide transition component for toast animations
 */
function SlideTransition(props: SlideProps) {
  return <Slide {...props} direction="up" />;
}

/**
 * Individual Toast component that displays a single notification
 */
export const Toast: React.FC<ToastProps> = ({
  notification,
  onClose,
  position = { vertical: 'top', horizontal: 'right' },
}) => {
  const manualEmailLinkText = 'Click here to send it manually.';
  const hasInlineManualEmailLink =
    Boolean(notification.onAction) &&
    notification.actionLabel?.toLowerCase() === 'here' &&
    notification.message.includes(manualEmailLinkText);

  const handleClose = (_event?: React.SyntheticEvent | Event, reason?: SnackbarCloseReason) => {
    if (reason === 'clickaway') {
      return;
    }
    onClose(notification.id);
  };

  const handleAction = () => {
    if (notification.onAction) {
      notification.onAction();
    }
    onClose(notification.id);
  };

  const renderMessage = () => {
    if (!hasInlineManualEmailLink) {
      return notification.message;
    }

    const [beforeLinkText] = notification.message.split(manualEmailLinkText);

    return (
      <>
        {beforeLinkText}
        {'Click '}
        <Button
          onClick={handleAction}
          sx={{
            minWidth: 0,
            padding: 0,
            margin: 0,
            textTransform: 'none',
            textDecoration: 'underline',
            fontWeight: 700,
            fontSize: 'inherit',
            lineHeight: 1,
            verticalAlign: 'baseline',
            color: '#90caf9',
          }}
        >
          here
        </Button>
        {' to send it manually.'}
      </>
    );
  };

  /**
   * Get the appropriate alert title based on severity
   */
  const getAlertTitle = (severity: ToastSeverity): string => {
    switch (severity) {
      case ToastSeverity.ERROR:
        return 'Error';
      case ToastSeverity.WARNING:
        return 'Warning';
      case ToastSeverity.INFO:
        return 'Information';
      case ToastSeverity.SUCCESS:
        return 'Success';
      default:
        return 'Notification';
    }
  };

  return (
    <Snackbar
      open={true}
      autoHideDuration={notification.autoHideDuration}
      onClose={handleClose}
      anchorOrigin={position}
      TransitionComponent={SlideTransition}
      sx={{
        '& .MuiSnackbarContent-root': {
          padding: 0,
        },
      }}
    >
      <Alert
        severity={notification.severity}
        variant="filled"
        sx={{
          'minWidth': '300px',
          'maxWidth': '500px',
          'boxShadow': 3,
          '& .MuiAlert-message': {
            width: '100%',
          },
        }}
        action={
          <>
            {!hasInlineManualEmailLink && notification.actionLabel && notification.onAction && (
              <Button
                color="inherit"
                size="small"
                onClick={handleAction}
                sx={{
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                  fontSize: '0.75rem',
                }}
              >
                {notification.actionLabel}
              </Button>
            )}
            {notification.dismissible && (
              <IconButton size="small" aria-label="close" color="inherit" onClick={handleClose}>
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </>
        }
      >
        <AlertTitle
          sx={{
            fontWeight: 'bold',
            fontSize: '0.875rem',
            marginBottom: 0.5,
          }}
        >
          {getAlertTitle(notification.severity)}
        </AlertTitle>
        {renderMessage()}
      </Alert>
    </Snackbar>
  );
};

export default Toast;
